import {
  Box,
  Container,
  Typography,
  Grid,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  Avatar,
  Chip,
} from "@material-ui/core";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { collectionOrdersStyles } from "../utils/styles/collectionOrdersStyles";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import InfiniteScroll from "react-infinite-scroll-component";
import { HeaderWithButtons, Searchbar } from "../components-molecules";
import { useHistory, useLocation } from "react-router-dom";
import { getMerchantsList, MerchantOrganisationSearch, MerchantOrganisationSearchResponseModel } from "../redux/actions/merchants";

const pageSize = 25;


export function MerchantList() {
  const location = useLocation();
  const history = useHistory();

  const classes = collectionOrdersStyles();
  const [merchants, setMerchants] = useState<MerchantOrganisationSearch[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const currentPageRef = useRef(0);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // URL Filters
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const page = useMemo(() => Number(urlParams.get('page')), [urlParams]) || 0;
  const query = useMemo(() => urlParams.get('query'), [urlParams]) || '';
  const searchOpen = useMemo(() => urlParams.get('searchOpen') === 'true', [urlParams]) || false;

  const setParam = useCallback((key: string, value: any) => {
    urlParams.delete(key);
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          urlParams.append(key, v);
        });
      } else {
        urlParams.set(key, value);
      }
      history.push({ pathname: location.pathname, search: urlParams.toString() });
    }
  }, [urlParams, history, location.pathname]);

  const deleteParam = useCallback((key: string) => {
    urlParams.delete(key);
    history.push({ pathname: location.pathname, search: urlParams.toString() });
  }, [urlParams, history, location.pathname]);

  const setPage = useCallback((p: number) => {
    setParam('page', p);
  }, [setParam]);

  const getMerchants = useCallback((currentPage = 0) => {
    setLoading(true);
    
    getMerchantsList(
      {
        Page: currentPage,
        Size: pageSize,
        Query: query
      },
      (data: MerchantOrganisationSearchResponseModel) => {
        if (currentPage === 0) {
          setMerchants(data.Organisations);
        } else {
          setMerchants((prev) => ([
            ...prev,
            ...data.Organisations
          ]));
        }
        // If we get fewer results than pageSize, we've reached the end
        setHasMore(data.Organisations.length === pageSize);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching merchants:', err);
        setLoading(false);
      }
    );
  }, [query]);

  const refreshList = useCallback(() => {
    setMerchants([]); // Clear list on refresh
    currentPageRef.current = 0; // Reset page to 0
    setPage(0); // Reset page to 0
    setHasMore(true); // Reset hasMore
    getMerchants(0); // Fetch first page
  }, [getMerchants, setPage]);

  // Initialize search value from URL
  useEffect(() => {
    setSearchValue(query);
  }, [query]);

  // Debounced search - only update URL after user stops typing
  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce the URL update by 500ms
    searchTimeoutRef.current = setTimeout(() => {
      setParam('query', value);
    }, 500);
  }, [setParam]);

  // Only refresh when query changes, not when search opens/closes
  const prevQueryRef = useRef<string | null>(null);
  
  useEffect(() => {
    // Only refresh if query actually changed
    if (prevQueryRef.current !== query) {
      prevQueryRef.current = query;
      refreshList();
    }
  }, [query, refreshList]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = currentPageRef.current + 1;
      getMerchants(nextPage);
      currentPageRef.current = nextPage;
      setPage(nextPage);
    }
  }, [loading, hasMore, getMerchants, setPage]);

  const renderHeader = () => {
    return (
      <HeaderWithButtons title="Merchants">
        <HeaderWithButtons.ButtonArea>
          <IconButton
            data-automation="search-button"
            className={classes.searchIconButton}
            color="primary"
            onClick={() => searchOpen ? deleteParam('searchOpen') : setParam('searchOpen', true)}
          >
            <SearchRoundedIcon
              color="primary"
              className={classes.searchIcon}
            />
          </IconButton>
        </HeaderWithButtons.ButtonArea>
      </HeaderWithButtons>
    );
  };

  const renderMerchantCard = (merchant: MerchantOrganisationSearch) => {
    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={merchant.Id}>
        <Card
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
          }}
          onClick={() => history.push(`/admin/merchant/${merchant.Id}`)}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
          }}
        >
          <CardContent style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px' }}>
            <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <Avatar
                src={merchant.LogoUrl}
                style={{
                  width: '48px',
                  height: '48px',
                  marginRight: '12px',
                  backgroundColor: '#f5f5f5',
                }}
              >
                {merchant.Name.charAt(0).toUpperCase()}
              </Avatar>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h6"
                  component="h3"
                  style={{
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: 1.3,
                    marginBottom: '4px',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                  }}
                >
                  {merchant.Name}
                </Typography>
              </Box>
            </Box>
            
            <Box style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {merchant.ShopsCount} {merchant.ShopsCount === 1 ? 'Shop' : 'Shops'}
              </Typography>
              <Chip
                label={merchant.Archived ? "Archived" : "Active"}
                size="small"
                style={{
                  backgroundColor: merchant.Archived ? '#ffebee' : '#e8f5e8',
                  color: merchant.Archived ? '#c62828' : '#2e7d32',
                  fontSize: '11px',
                  fontWeight: 600,
                  height: '20px',
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Container className={classes.root}>
      {renderHeader()}
      {!loading &&
        <Searchbar
          query={searchValue}
          isOpen={searchOpen}
          placeholder="Search Merchants"
          onChange={(e) => {
            handleSearchChange(e.target.value);
          }}
          onCancel={() => {
            setSearchValue('');
            deleteParam('query');
            deleteParam('searchOpen');
          }}
        />
      }
      <Box mt={3}>
        {query.length > 0 && merchants.length === 0 && !loading ?
          <Grid container item className={classes.uPaddingLeft} style={{ marginTop: '40px' }}>
            <Grid container item style={{ maxWidth: '320px' }}>
              <Typography variant="h6" component="span">
                0 results found
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                component="span"
                style={{ marginTop: '12px' }}>
                Adjust your filtering and try again
              </Typography>
            </Grid>
          </Grid>
          : null}
        {loading && page === 0 ? (
          <Grid
            item
            container
            justifyContent="center"
            alignItems="center"
            style={{ marginTop: "96px" }}
          >
            <CircularProgress />
          </Grid>
        ) : merchants.length > 0 ? (
          <InfiniteScroll
            style={{ overflow: "hidden" }}
            dataLength={merchants.length}
            next={loadMore}
            scrollThreshold={0.8}
            hasMore={hasMore}
            loader={
              <Container style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Container>
            }>
            <Grid container spacing={2}>
              {merchants.map((merchant) => renderMerchantCard(merchant))}
            </Grid>
          </InfiniteScroll>
        ) : (
          <Grid
            container
            item
            justifyContent="center"
            alignItems="center"
            style={{ marginTop: "96px" }}
          >
            <Grid
              container
              item
              justifyContent="center"
              alignItems="center"
              style={{ maxWidth: "320px" }}
            >
              <Typography variant="h6" component="span" align="center">
                No merchants found
              </Typography>
            </Grid>
          </Grid>
        )}
      </Box>
    </Container>
  );
}

export default MerchantList;
